import React from 'react';
import {
  facetDimensionLabel,
  isNarrowableDimension,
  type RefusalFacet,
  type RefusalFailure,
} from './toolError.js';

// S-DM-005 — the scope panel. A refusal is rendered as a NEXT ACTION, never as
// "no results found": the human message, the dimensions that would make the
// request answerable, and (when the payload carries them) the facet values as
// clickable controls that re-issue the search narrowed on that dimension.
//
// AC4: with no facets the facet region is not rendered at all — no empty region
// and no dead control.
//
// AC6 (EXP-DM-5): a control is rendered per facet DIMENSION, not for
// `repository` alone. The unknown-branch refusal — the only facet-bearing
// refusal the platform actually emits today — carries a `branch` facet, and
// used to render zero clickable controls, leaving the valid branches as prose:
// a dead end with a helpful sentence, which is the exact state this story
// exists to remove. A dimension outside the frozen `?q=`/`?repo=`/`?branch=`
// URL contract cannot be narrowed on, so its values are listed with the reason
// stated rather than rendered as buttons that would do nothing.

interface ScopePanelProps {
  failure: RefusalFailure;
  /**
   * Re-issues the search narrowed on ONE dimension. `dimension` is the facet's
   * wire dimension (`repository`, `branch`, …) — the narrowing is dimension-
   * aware, not hardcoded to repository.
   */
  onFacetSelect: (dimension: string, value: string) => void;
}

function formatCount(count: number | null): string {
  return count === null ? '' : ` (${count.toLocaleString()})`;
}

/**
 * The dimensions to render. Prefers the full `facets` list; falls back to
 * `repositoryFacets` so a caller holding a pre-AC6 `RefusalFailure` (one built
 * without `facets`) still renders exactly as it did before.
 */
function facetGroups(failure: RefusalFailure): RefusalFacet[] {
  if (failure.facets.length > 0) return failure.facets;
  return failure.repositoryFacets.length > 0
    ? [{ dimension: 'repository', values: failure.repositoryFacets }]
    : [];
}

export default function ScopePanel({
  failure,
  onFacetSelect,
}: ScopePanelProps): React.ReactElement {
  const { message, dimensions, scope } = failure;
  const groups = facetGroups(failure);

  return (
    <div className="scope-panel" role="status" data-testid="scope-panel">
      <div className="scope-panel-title" data-testid="scope-panel-message">
        {message}
      </div>

      {scope?.totalCount !== null && scope?.totalCount !== undefined && (
        <div className="scope-panel-total" data-testid="scope-panel-total">
          {scope.isApproximate ? 'About ' : ''}
          {scope.totalCount.toLocaleString()} matches across the corpus
        </div>
      )}

      {dimensions.length > 0 && (
        <div className="scope-panel-dimensions" data-testid="scope-panel-dimensions">
          Narrow by: {dimensions.join(', ')}
        </div>
      )}

      {/* AC4: rendered only when facets exist. AC6: one region per dimension. */}
      {groups.map((group) =>
        isNarrowableDimension(group.dimension) ? (
          <div
            key={group.dimension}
            className="scope-panel-facets"
            data-testid="scope-panel-facets"
            data-facet-dimension={group.dimension}
          >
            <div className="scope-panel-facets-label">{facetDimensionLabel(group.dimension)}</div>
            {group.values.map((facet) => (
              <button
                key={facet.value}
                type="button"
                className="scope-panel-facet"
                data-testid="scope-panel-facet"
                data-facet-dimension={group.dimension}
                data-facet-value={facet.value}
                onClick={() => onFacetSelect(group.dimension, facet.value)}
              >
                {facet.value}
                {formatCount(facet.count)}
              </button>
            ))}
          </div>
        ) : (
          // AC6's second half: no control, and the panel says why — never a
          // button that cannot change the query.
          <div
            key={group.dimension}
            className="scope-panel-facets scope-panel-facets-inert"
            data-testid="scope-panel-facets-inert"
            data-facet-dimension={group.dimension}
          >
            <div className="scope-panel-facets-label">{facetDimensionLabel(group.dimension)}</div>
            <div className="scope-panel-facets-reason" data-testid="scope-panel-facets-reason">
              {`Not yet filterable from search — ${group.values
                .map((f) => f.value)
                .join(', ')}`}
            </div>
          </div>
        ),
      )}
    </div>
  );
}
